import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { Input } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import { LANGUAGE_CODE, PUBLIC_PAGE_ROUTES } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { createRegisterValidationSchema } from '@/pages/Register/Validation';
import { useRegisterMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { RegisterCredentials } from '@/types/UserTypes';

const SUPPORTED_LANGUAGE_CODES = [
  LANGUAGE_CODE.EnglishUS,
  LANGUAGE_CODE.FrenchFR,
  LANGUAGE_CODE.DutchBE,
  LANGUAGE_CODE.GermanDE
];

type CeremoniesPerMonthOption = {
  value: RegisterCredentials['ceremoniesPerMonth'];
  label?: string;
  labelKey?: string;
};

const CEREMONIES_PER_MONTH_OPTIONS: CeremoniesPerMonthOption[] = [
  { value: 'Less than 10', labelKey: 'regCeremoniesPerMonthLt10' },
  { value: '11 - 20', label: '11 - 20' },
  { value: '21 - 30', label: '21 - 30' },
  { value: 'More than 30', labelKey: 'regCeremoniesPerMonthGt30' }
];

export const RegisterForm = () => {
  const navigate = useNavigate();
  const [t, i18n] = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const storedLanguageRaw = localStorage.getItem('currentLanguage');
  const storedLanguageValue =
    storedLanguageRaw === null || storedLanguageRaw === '' ? NaN : Number(storedLanguageRaw);
  let initialLanguage = LANGUAGE_CODE.DutchBE;

  if (
    !Number.isNaN(storedLanguageValue) &&
    SUPPORTED_LANGUAGE_CODES.includes(storedLanguageValue as LANGUAGE_CODE)
  ) {
    initialLanguage = storedLanguageValue as LANGUAGE_CODE;
  }

  const initialValues: RegisterCredentials = {
    firstName: '',
    lastName: '',
    email: '',
    organizationName: '',
    defaultLanguage: initialLanguage as LANGUAGE_CODE,
    ceremoniesPerMonth: 'Less than 10',
    promoCode: ''
  };
  const validationSchema = useMemo(() => createRegisterValidationSchema(), [i18n.language]);

  useEffect(() => {
    if (
      Number.isNaN(storedLanguageValue) ||
      !SUPPORTED_LANGUAGE_CODES.includes(storedLanguageValue as LANGUAGE_CODE)
    ) {
      localStorage.setItem('currentLanguage', String(LANGUAGE_CODE.DutchBE));
    }

    void i18n.changeLanguage(LANGUAGE_CODE[initialLanguage]);
  }, [i18n, initialLanguage, storedLanguageValue]);

  const { mutate: register } = useRegisterMutation(queryClient, {
    onSuccess: (response) => {
      const status = response?.status;
      const isHttpSuccess = typeof status === 'number' && status >= 200 && status < 300;

      if (!isHttpSuccess) {
        setIsSubmitting(false);
        showToast(ToastType.ERROR, t('somethingWentWrong'));
        return;
      }

      setIsSubmitting(false);
      navigate(PUBLIC_PAGE_ROUTES.CHECK_EMAIL);
    },
    onError: (error) => {
      setIsSubmitting(false);
      const message =
        error.response?.data?.ErrorMessages?.[0]?.DisplayMessage ?? t('somethingWentWrong');
      showToast(ToastType.ERROR, message);
    }
  });

  const handleSubmit = async (values: RegisterCredentials) => {
    const storedLanguageRawAtSubmit = localStorage.getItem('currentLanguage');
    const storedLanguageValueAtSubmit =
      storedLanguageRawAtSubmit === null || storedLanguageRawAtSubmit === ''
        ? NaN
        : Number(storedLanguageRawAtSubmit);

    const submitDefaultLanguage =
      !Number.isNaN(storedLanguageValueAtSubmit) &&
      SUPPORTED_LANGUAGE_CODES.includes(storedLanguageValueAtSubmit as LANGUAGE_CODE)
        ? (storedLanguageValueAtSubmit as LANGUAGE_CODE)
        : LANGUAGE_CODE.DutchBE;

    setIsSubmitting(true);
    register({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      organizationName: values.organizationName.trim(),
      defaultLanguage: submitDefaultLanguage,
      ceremoniesPerMonth: values.ceremoniesPerMonth,
      promoCode: values.promoCode?.trim() || undefined
    });
  };

  return (
    <div
      data-testid={TestIds.registration.form}
      className='flex size-full flex-col justify-center rounded-3xl bg-white px-4 shadow-[0_4px_14px_rgba(43,41,38,0.08)] lg:gap-8 lg:px-16'>
      <div className='flex flex-col md:gap-y-2 md:pt-6'>
        <Label
          text={t('regFormTrial')}
          type={LABEL_TYPE.H3}
          className='!text-lg !text-primary-blue'
        />
        <Label
          text={t('regFormTitle')}
          type={LABEL_TYPE.H1}
          className='!font-newsreader !font-semibold !text-primary-black'
        />
        <Label
          text={t('regFormSubtitle')}
          type={LABEL_TYPE.PARAGRAPH1}
          className='!text-slate-gray'
        />
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}>
        {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
          <Form className='flex flex-col gap-y-4'>
            <div className='flex flex-col gap-y-4 md:flex-row md:gap-x-4'>
              <Input
                id='firstName'
                type='text'
                dataTestId={TestIds.registration.firstNameInput}
                label={t('firstName')}
                placeholder={t('firstName')}
                value={values.firstName}
                handleChange={(value) => {
                  setFieldValue('firstName', value.trimStart(), false);
                }}
                onBlur={() => setFieldTouched('firstName', true)}
                errorMessage={touched.firstName ? errors.firstName : undefined}
                isInvalid={!!errors.firstName && touched.firstName}
                width='flex-1'
              />
              <Input
                id='lastName'
                type='text'
                dataTestId={TestIds.registration.lastNameInput}
                label={t('lastName')}
                placeholder={t('lastName')}
                value={values.lastName}
                handleChange={(value) => {
                  setFieldValue('lastName', value.trimStart(), false);
                }}
                onBlur={() => setFieldTouched('lastName', true)}
                errorMessage={touched.lastName ? errors.lastName : undefined}
                isInvalid={!!errors.lastName && touched.lastName}
                width='flex-1'
              />
            </div>
            <Input
              id='email'
              type='text'
              dataTestId={TestIds.registration.emailInput}
              label={t('email')}
              placeholder={t('email')}
              value={values.email}
              handleChange={(value) => {
                setFieldValue('email', value.trimStart(), false);
              }}
              onBlur={() => setFieldTouched('email', true)}
              errorMessage={touched.email ? errors.email : undefined}
              isInvalid={!!errors.email && touched.email}
            />

            <Input
              id='organizationName'
              type='text'
              dataTestId={TestIds.registration.organizationNameInput}
              label={t('organizationName')}
              placeholder={t('organizationName')}
              value={values.organizationName}
              handleChange={(value) => {
                setFieldValue('organizationName', value.trimStart(), false);
              }}
              onBlur={() => setFieldTouched('organizationName', true)}
              errorMessage={touched.organizationName ? errors.organizationName : undefined}
              isInvalid={!!errors.organizationName && touched.organizationName}
            />

            <div className='space-y-2.5'>
              <Label text={`${t('regCeremoniesPerMonthLabel')}`} type={LABEL_TYPE.PARAGRAPH2} />
              <div className='grid grid-cols-4 gap-2'>
                {CEREMONIES_PER_MONTH_OPTIONS.map((option) => {
                  const isSelected = values.ceremoniesPerMonth === option.value;
                  const optionLabel = option.labelKey ? t(option.labelKey) : (option.label ?? '');

                  return (
                    <button
                      key={option.value}
                      type='button'
                      className={`h-10 rounded-xl border px-2 text-sm transition-colors ${
                        isSelected
                          ? 'border-primary-blue bg-primary-blue text-white'
                          : 'hover:bg-blue-powder/20 border-secondary-blue-gray bg-primary-white text-secondary-gray hover:border-primary-blue hover:text-primary-blue'
                      }`}
                      onClick={() => {
                        setFieldValue('ceremoniesPerMonth', option.value, false);
                      }}>
                      {optionLabel}
                    </button>
                  );
                })}
              </div>
            </div>

            <Input
              id='promoCode'
              type='text'
              label={`${t('regPromoCodePlaceholder')} (${t('regOptional')})`}
              placeholder={t('regPromoCodePlaceholder')}
              value={values.promoCode}
              handleChange={(value) => {
                setFieldValue('promoCode', value.trimStart(), false);
              }}
            />

            <Button
              dataTestId={TestIds.registration.submitButton}
              type='submit'
              className='mt-4 w-full'
              disabled={isSubmitting}>
              {t('createAccount')}
            </Button>
            <div className='flex justify-center pt-2'>
              <span className='flex gap-x-2 text-sm text-primary-black'>
                <Label text={t('alreadyHaveAnAccount')} type={LABEL_TYPE.PARAGRAPH2} />
                <Link
                  data-testid={TestIds.registration.loginLink}
                  to={PUBLIC_PAGE_ROUTES.LOGIN}
                  className='text-sm text-primary-blue hover:underline'>
                  {t('login')}
                </Link>
              </span>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
